import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../context/ThemeContext';

// Icons
const StarIcon = ({ filled }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M19 12l-7 7-7-7" />
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
    <path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const WishListCardMenu = ({
  isOpen,
  onClose,
  card,
  priorityCount,
  onUpdate,
  showToast,
}) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(null);

  if (!isOpen || !card) return null;

  const isPriority = card.is_priority;
  const canPromote = !isPriority && priorityCount < 20;
  const canDemote = isPriority;

  const handleMoveToPriority = async () => {
    if (!canPromote) {
      showToast?.('Top 20 is full - remove one first', 'warning');
      onClose();
      return;
    }

    setLoading('promote');
    try {
      const { error } = await supabase
        .from('wish_list')
        .update({ is_priority: true })
        .eq('id', card.id);

      if (error) throw error;
      onUpdate?.();
      onClose();
    } catch (e) {
      console.error('Error promoting card:', e);
      showToast?.('Failed to move card', 'error');
      setLoading(null);
    }
  };

  const handleMoveToMore = async () => {
    setLoading('demote');
    try {
      const { error } = await supabase
        .from('wish_list')
        .update({ is_priority: false })
        .eq('id', card.id);

      if (error) throw error;
      onUpdate?.();
      onClose();
    } catch (e) {
      console.error('Error demoting card:', e);
      showToast?.('Failed to move card', 'error');
      setLoading(null);
    }
  };

  const handleRemove = async () => {
    setLoading('remove');
    try {
      const { error } = await supabase
        .from('wish_list')
        .delete()
        .eq('id', card.id);

      if (error) throw error;
      onUpdate?.();
      onClose();
    } catch (e) {
      console.error('Error removing card:', e);
      showToast?.('Failed to remove card', 'error');
      setLoading(null);
    }
  };

  const cardImageUrl = card.image_url
    ? `${card.image_url}/low.webp`
    : `https://assets.tcgdex.net/en/${card.set_id}/${card.tcgdex_card_id?.split('-')[1] || ''}/low.webp`;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ ...styles.backdrop, backgroundColor: theme.overlay }}
            onClick={onClose}
          />

          {/* Menu */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{ ...styles.menu, backgroundColor: theme.bgSheet }}
          >
            {/* Handle */}
            <div style={styles.handle} />

            {/* Card Preview */}
            <div style={{ ...styles.cardPreview, borderColor: theme.border }}>
              <img
                src={cardImageUrl}
                alt={card.card_name}
                style={styles.cardImage}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div style={styles.cardInfo}>
                <span style={{ ...styles.cardName, color: theme.text }}>{card.card_name}</span>
                <span style={{ ...styles.cardSet, color: theme.textSecondary }}>{card.set_name}</span>
                {card.variant && card.variant !== 'Normal' && (
                  <span style={{ ...styles.cardVariant, color: theme.accent }}>{card.variant}</span>
                )}
              </div>
              {isPriority && (
                <div style={{ ...styles.priorityBadge, backgroundColor: theme.warningBg, color: theme.warning }}>
                  <StarIcon filled />
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={styles.actions}>
              {isPriority ? (
                <button
                  style={{
                    ...styles.actionButton,
                    backgroundColor: theme.bgCard,
                    borderColor: theme.border,
                    color: theme.text,
                    opacity: loading === 'demote' ? 0.6 : 1,
                  }}
                  onClick={handleMoveToMore}
                  disabled={loading}
                >
                  <ArrowDownIcon />
                  <span>{loading === 'demote' ? 'Moving...' : 'Move to More'}</span>
                </button>
              ) : (
                <button
                  style={{
                    ...styles.actionButton,
                    backgroundColor: canPromote ? theme.warningBg : theme.bgCard,
                    borderColor: canPromote ? theme.warning : theme.border,
                    color: canPromote ? theme.warning : theme.textMuted,
                    opacity: loading === 'promote' ? 0.6 : 1,
                  }}
                  onClick={handleMoveToPriority}
                  disabled={loading || !canPromote}
                >
                  <ArrowUpIcon />
                  <span>
                    {!canPromote
                      ? 'Top 20 Full'
                      : loading === 'promote'
                        ? 'Moving...'
                        : 'Move to Top 20'}
                  </span>
                </button>
              )}

              <button
                style={{
                  ...styles.actionButton,
                  backgroundColor: theme.errorBg,
                  borderColor: theme.error,
                  color: theme.error,
                  opacity: loading === 'remove' ? 0.6 : 1,
                }}
                onClick={handleRemove}
                disabled={loading}
              >
                <TrashIcon />
                <span>{loading === 'remove' ? 'Removing...' : 'Remove from Wish List'}</span>
              </button>

              <button
                style={{
                  ...styles.actionButton,
                  backgroundColor: theme.bgCard,
                  borderColor: theme.border,
                  color: theme.textSecondary,
                }}
                onClick={onClose}
                disabled={loading}
              >
                <CloseIcon />
                <span>Cancel</span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2000,
  },
  menu: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 16px',
    paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
    zIndex: 2001,
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: {
    width: '36px',
    height: '4px',
    backgroundColor: '#3a3a3a',
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  cardPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid',
    marginBottom: '16px',
  },
  cardImage: {
    width: '50px',
    height: '70px',
    objectFit: 'cover',
    borderRadius: '6px',
    flexShrink: 0,
  },
  cardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  cardName: {
    fontSize: '15px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSet: {
    fontSize: '13px',
  },
  cardVariant: {
    fontSize: '12px',
    fontWeight: '500',
  },
  priorityBadge: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: '1px solid',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
};

export default WishListCardMenu;
